"use client";
import React, { useEffect, useRef, useState } from "react";
import Image, { StaticImageData } from "next/image";
import gsap from "gsap";

import Liq1 from "../../../public/assets/gallery/liq1.png";
import Liq2 from "../../../public/assets/gallery/liq2.png";
import Liq3 from "../../../public/assets/gallery/liq3.png";
import Liq4 from "../../../public/assets/gallery/liq4.png";
import Liq5 from "../../../public/assets/gallery/liq5.png";
import Ren3 from "../../../public/assets/gallery/slide3.jpg";
import Flat1 from "../../../public/assets/gallery/flat-1.png";
import Flat2 from "../../../public/assets/gallery/flat-2.png";
import Flat3 from "../../../public/assets/gallery/slide4.jpg";
import Flat4 from "../../../public/assets/gallery/flat4.png";
import Flat5 from "../../../public/assets/gallery/flat5.png";
import Mirror3 from "../../../public/assets/gallery/mirror-3.png";
import Mirror2 from "../../../public/assets/gallery/mirror-2.png";
import Mirror1 from "../../../public/assets/gallery/mirror-1.png";
import NoImg from "../../../public/assets/gallery/no-img.png";
import food1 from "../../../public/assets/gallery/gal4.jpg";

import food2 from "../../../public/assets/gallery/gal2.jpg";

import food3 from "../../../public/assets/gallery/gal1.jpg";
import Ren2 from "../../../public/assets/gallery/ren2.png";
import Ren1 from "../../../public/assets/gallery/ren1.png";

const tabNames = [
  "All",
  "Flat Glass",
  "Mirror Glass",
  "Liquor Bottles",
  "Food Bottles",
  "Renewables",
] as const;

type TabName = (typeof tabNames)[number];

interface GalleryImages {
  All: StaticImageData[];
  "Flat Glass": StaticImageData[];
  "Mirror Glass": StaticImageData[];
  "Liquor Bottles": StaticImageData[];
  "Food Bottles": StaticImageData[];
  Renewables: StaticImageData[];
}

const images: GalleryImages = {
  All: [Flat1, Mirror1, Liq2, food1, Ren3],
  "Flat Glass": [Flat1, Flat2,  Flat3,Flat4, Flat5 ],
  "Mirror Glass": [Mirror3, Mirror2, NoImg, Mirror1, NoImg],
  "Liquor Bottles": [Liq1, Liq2, Liq3, Liq4, Liq5],
  "Food Bottles": [food1, food2, food3, NoImg, NoImg],
  Renewables: [Ren3, Ren2, NoImg, Ren1, NoImg],
};

// 🧩 Collage logic reused for all images
const getCollageClasses = (index: number) => {
  const layouts = [
    "col-span-2 row-span-2",
    "col-span-1 row-span-1",
    "col-span-1 row-span-1",
    "col-span-1 row-span-2",
    "col-span-2 row-span-1",
  ];
  return layouts[index % layouts.length];
};

const GalleryTabs = () => {
  const [activeTab, setActiveTab] = useState<TabName>("All");
  const galleryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (galleryRef.current) {
      const items = galleryRef.current.querySelectorAll(".gallery-item");

      gsap.fromTo(
        items,
        { autoAlpha: 0, y: 30, scale: 0.95 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          ease: "power2.out",
          stagger: 0.1,
        }
      );
    }
  }, [activeTab]);

  return (
    <section className="py-[90px] gray-background bg-cover bg-bottom border-t border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-10 flex-wrap gap-4">
          <h2 className="text-4xl font-bold text-[#282930]">
            Product <span className="red-gradient-text">Gallery</span>
          </h2>
          <div className="flex flex-wrap flex-1 gap-3 justify-end">
            {tabNames.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeTab === tab
                    ? "bg-red-600 text-white shadow"
                    : "bg-white border border-red-600 text-red-600 hover:bg-red-100"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div
          className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 auto-rows-[150px] gap-4"
          ref={galleryRef}
        >
          {(activeTab === "All"
            ? images[activeTab].slice(0, 5)
            : images[activeTab]
          ).map((img, index) => (
            <div
              key={index}
              className={`gallery-item relative overflow-hidden rounded-xl shadow-xl bg-white border border-gray-200 hover:scale-[1.02] transition-transform duration-300 ${getCollageClasses(index)}`}
            >
              <Image
                src={img}
                alt={`gallery-${index}`}
                fill
                className="object-cover w-full h-full"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GalleryTabs;
